import {
	Badge,
	Button,
	Listbox,
	ListboxItem,
	Popover,
	PopoverContent,
	PopoverTrigger,
	Spinner,
} from "@heroui/react";
import { Icon } from "@iconify/react";

import { useIsMounted, useLanguage, useUserInfos } from "@onesolution/hooks";
import type { TranslationsKeys } from "@onesolution/hooks/useLanguage";
import {
	getCurrentUserNotifications,
	getCurrentUserNotificationsCount,
} from "@onesolution/serverFn/user/getCurrentUserNotifications";
import { markNotifAsReadOnServer } from "@onesolution/serverFn/user/markNotifAsRead";
import { useState } from "react";
import Chip from "./Chip";

export default function NotificationsButton() {
	const isMounted = useIsMounted();
	const { t } = useLanguage();
	const [loadings, setLoadings] = useState<string[]>([]);
	const userInfos = useUserInfos();
	const [open, setOpen] = useState(false);
	const { data: notifCount } = getCurrentUserNotificationsCount.useQuery();
	const {
		data: notifs,
		refetch,
		isFetchedAfterMount,
	} = getCurrentUserNotifications.useQuery({
		enabled: false,
	});
	const { mutate: markAsRead } = markNotifAsReadOnServer.useMutation();

	if (!isMounted) return null;

	return (
		<Popover
			placement="right-start"
			onOpenChange={() => {
				refetch();
				setOpen(!open);
			}}
			isOpen={open}
		>
			<PopoverTrigger>
				<Button isIconOnly color="default" variant={"light"} size="lg">
					<Badge
						color="danger"
						content={notifCount}
						size="sm"
						hidden={!notifCount}
						className="p-1.5 text-xs "
					>
						<Icon
							color={notifCount ? "red" : ""}
							className="size-5 transition-all duration-500"
							icon={notifCount ? "solar:bell-bold" : "solar:bell-bold-duotone"}
						/>
					</Badge>
				</Button>
			</PopoverTrigger>
			<PopoverContent>
				<div className="w-80 flex flex-col gap-2 max-h-96 overflow-auto">
					{!isFetchedAfterMount ? (
						<Spinner className="m-10" />
					) : (
						<Listbox aria-label="Notifications">
							{notifs?.map((notification) => {
								const isLoading = loadings.includes(notification.id);
								const isNew = !notification.processed.includes(userInfos.id);
								return (
									<ListboxItem
										key={notification.id}
										onPress={async () => {
											setOpen(false);
											await markAsRead({ id: notification.id });
											window.location.href = `/app/operations?operation=${notification.pulseId}&tab=activity`;
										}}
										className="py-4 px-5 hover:bg-default-100 group "
									>
										<div className="flex items-start gap-3">
											<span
												className={`mt-2 h-2.5 w-2.5 rounded-full shrink-0 ${isNew ? "bg-danger" : "bg-gray-300"}`}
											/>
											<div className="flex-1 w-52">
												<div className="flex gap-4 items-start">
													<Chip
														color={
															notification.boardName === "briefs"
																? "primary"
																: "secondary"
														}
														className="opacity-70"
													>
														{t(
															`boards.${notification.boardName}` as TranslationsKeys,
														)}
													</Chip>
													<Chip className="opacity-70">
														{notification.pulseName}
													</Chip>
												</div>
												<p className="text-sm mt-1 font-medium text-foreground">
													{notification.name}
												</p>
												<div className="mt-1 flex items-center gap-2 text-xs text-default-500">
													<span>
														{new Date(notification.time).toLocaleDateString()}
													</span>
													<span>•</span>
													<span>
														{new Date(notification.time).toLocaleTimeString()}
													</span>
												</div>
											</div>
											<Button
												isIconOnly
												color="default"
												disabled={isLoading}
												variant={"light"}
												size="lg"
												onPress={async (e) => {
													if (isLoading) return;

													setLoadings([...loadings, notification.id]);
													markAsRead(
														{
															id: notification.id,
															delete: true,
														},
														{
															onSettled: () => {
																refetch().then(() => {
																	setLoadings(
																		loadings.filter(
																			(l) => l !== notification.id,
																		),
																	);
																});
															},
														},
													);
												}}
												className="opacity-0 group-hover:opacity-100"
											>
												{isLoading ? (
													<Spinner />
												) : (
													<Icon
														className="size-5 transition-all duration-500"
														icon="solar:trash-bin-2-bold-duotone"
													/>
												)}
											</Button>
										</div>
									</ListboxItem>
								);
							}) || null}
						</Listbox>
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
}
